
/**
 * Returns the base hostname for the given host. This is defined as the TLD and the first-level
 * hostname (but no sub-hosts). So for example:
 *
 * www.wayfair.com -> wayfair.com
 * a.b.wayfair.com -> wayfair.com
 * wayfair.com -> wayfair.com
 * justahost -> justahost
 * www.amazon.co.uk -> amazon.co.uk
 *
 * Normally this will return a string in the form "a.b", but if the hostname has no periods, then
 * this will return exactly the same thing as the input host.
 *
 * The string returned by this method is always going to be a trailing subset of the input host such
 * that host.endsWith(getBaseHost(host)).
 *
 * @param host The hostname to get the base host for.
 * @return The base hostname for the given host, or null if the input is null.
 */
export function getBaseHost(host: string|null): string|null {
  if (host == null) return null;

  const lastdot = host.lastIndexOf('.');
  if (lastdot === -1) return host;

  let prevdot = host.lastIndexOf('.', lastdot - 1);
  if (prevdot === -1) return host;

  // special case for domains like www.amazon.co.uk, where we want to treat
  // the ".co.uk" as the TLD, even though it's not *really* the TLD
  // it's effectively used as such
  if (host.length - prevdot <= 6) {
      prevdot = host.lastIndexOf('.', prevdot - 1);
      if (prevdot === -1) return host;
  }

  return host.substring(prevdot + 1);
};

/**
 * 
 * @param url1 
 * @param url2 
 * @returns basehosts are equal or not
 */
export function compareBaseHosts(url1: string, url2: string): boolean {
  try {
    const parsedUrl1 = new URL(url1);
    const parsedUrl2 = new URL(url2);
  
    const baseHost1 = getBaseHost(parsedUrl1.hostname);
    const baseHost2 = getBaseHost(parsedUrl2.hostname);
    return baseHost1 === baseHost2;
  }
  catch(e) { 
    return false;
  }
}

/**
 * Joins a path against a base URL, using the same logic as the browser does when
 * handling relative links. If the provided baseUrlOrPath is an absolute path, then
 * this will be combined relative to the current page's URL, again using the browser's
 * logic for relative links.
 * 
 * If baseUrlOrPath doesn't have a trailing slash, this will assume one anyway.
 * 
 * @param pathOrUrl      Relative or absolute path to be joined to baseUrlOrPath. If starts with http, is returned unchanged.
 * @param baseUrlOrPath  The base URL to join pathOrUrl onto. If this isn't a full URL (doesn't start with http), then it's joined to windowLocationHref
 * @param serverUrl      The value of window.location.href, or the base base URL to join the other two parameters to. If null, window.location.href is used.
 */
export function joinURLs(pathOrUrl: string, baseUrlOrPath: string, windowLocationHref?: string) {
  if ( pathOrUrl.startsWith("http") ) {
    return pathOrUrl;
  }

  if ( windowLocationHref == null ) {
    windowLocationHref = window.location.href;
  }

  if ( !baseUrlOrPath.endsWith("/") ) {
    baseUrlOrPath += "/";
  }

  if ( baseUrlOrPath.startsWith("http") ) {
    return new URL(pathOrUrl, baseUrlOrPath).href;
  } else {
    const localBaseUrl = new URL(baseUrlOrPath, windowLocationHref);
    return new URL(pathOrUrl, localBaseUrl).href;
  }
}